/**
 * hud.js — DOM HUD controller（展示模式）
 *
 * Manages all DOM elements: top bar, progress bar, app-redirect hint, share button.
 * State updates are triggered by main.js after loading island data.
 * Upgrade actions are handled in the native App (Apple 3.1.1 compliance).
 */

import { LEVEL_META, computePct } from './levelMeta.js'
import { t } from './i18n/index.js'

// ── DOM element references ───────────────────────────────────────────────────
const $title = document.getElementById('planet-title')
const $subtitle = document.getElementById('planet-subtitle')
const $balance = document.getElementById('p-balance')
const $prgText = document.getElementById('progress-text')
const $prgPct = document.getElementById('progress-pct')
const $prgFill = document.getElementById('progress-fill')
const $btnUpg = document.getElementById('btn-upgrade')
const $loading = document.getElementById('loading')
const $toast = document.getElementById('toast')

// ── Loading screen ───────────────────────────────────────────────────────────
export function hideLoading() {
  $loading.classList.add('hidden')
  setTimeout(() => {
    $loading.style.display = 'none'
  }, 700)
}

// ── Main state update ────────────────────────────────────────────────────────
/**
 * @param {{level, total_p_spent, exp_points}} island
 * @param {number} balance  current P balance
 */
export function updateHUD(island, balance) {
  const level = island?.level ?? 1
  const totalSpent = island?.total_p_spent ?? 0
  const meta = LEVEL_META[Math.min(level, 4)]

  // Top bar
  $title.textContent = t('game.title', '時光星球')
  $subtitle.textContent = `Lv.${level} · ${t(meta.labelKey)}`
  $subtitle.style.color = meta.color

  // P balance
  $balance.textContent = `${balance} P`

  // Progress
  const pct = computePct(level, totalSpent)
  const nextAt = meta.nextAt

  $prgFill.style.width = `${pct}%`
  $prgFill.style.background = meta.color
  $prgPct.textContent = `${pct}%`

  if (nextAt == null) {
    $prgText.textContent = t('game.max_level', '已達最高星系')
    $prgPct.textContent = '✦'
  } else {
    const needed = Math.max(nextAt - totalSpent, 0)
    $prgText.textContent = t('game.progress_need', '升級進度 · 還需 {n}P', { n: needed })
  }

  // Upgrade hint (display-only — redirect to App)
  $btnUpg.disabled = true
  $btnUpg.textContent = t('game.back_to_app', '返回 App 注入能量')
  $btnUpg.style.background = 'rgba(255,255,255,0.08)'
  $btnUpg.style.color = 'rgba(255,255,255,0.5)'
  $btnUpg.style.border = '1px solid rgba(255,255,255,0.15)'
}

// ── Toast notification ───────────────────────────────────────────────────────
let _toastTimer = null

export function showToast(msg, duration = 2800) {
  $toast.textContent = msg
  $toast.classList.add('show')
  clearTimeout(_toastTimer)
  _toastTimer = setTimeout(() => $toast.classList.remove('show'), duration)
}

// ── Level-up celebration ─────────────────────────────────────────────────────
export function celebrateLevelUp(newLevel) {
  const lv = Math.min(newLevel, 4)
  const label = t(LEVEL_META[lv].labelKey)
  showToast(t('game.level_up', '⬆ 升級！進入「{label}」階段', { label }), 4000)

  // Brief colour flash on subtitle
  $subtitle.style.transform = 'scale(1.15)'
  setTimeout(() => {
    $subtitle.style.transform = 'scale(1)'
  }, 400)
}

// ── Share button handler ────────────────────────────────────────────────────
/**
 * @param {() => void} onShare
 */
export function bindShareButton(onShare) {
  document.getElementById('btn-share').addEventListener('click', onShare)
}
